/**
 * Patient age in whole years at a point in time, from ISO date strings.
 *
 * Reference ranges that depend on age use the age at the STUDY date, never
 * today, so a report re-opened years later still grades against the band
 * that applied when the scan was taken. Mirrors `age_in_years` in the
 * backend's `codings/reference_ranges.py`.
 *
 * Both inputs are compared on their calendar date (the first ten characters,
 * `YYYY-MM-DD`), so a timestamped study date and a date-only birth date
 * never disagree by a timezone offset.
 */
export function ageInYears(
  dateOfBirth: string | null | undefined,
  at: string | null | undefined
): number | null {
  const dob = calendarParts(dateOfBirth);
  const ref = calendarParts(at);
  if (!dob || !ref) return null;
  let years = ref.year - dob.year;
  if (ref.month < dob.month || (ref.month === dob.month && ref.day < dob.day)) {
    years -= 1;
  }
  return years >= 0 ? years : null;
}

function calendarParts(
  value: string | null | undefined
): { year: number; month: number; day: number } | null {
  if (!value || typeof value !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (!match) return null;
  const [, y, m, d] = match;
  const year = Number(y);
  const month = Number(m);
  const day = Number(d);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}
